import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAdminGrantCredits, useAdminLogActAs } from "@chotca/api-client";
import { Button, Card, FormField, Input, useToast } from "@chotca/ui";

const grantSchema = z.object({
  amount: z
    .number({ message: "Nhập số lượt hợp lệ" })
    .int("Số lượt phải là số nguyên")
    .min(1, "Số lượt tối thiểu là 1")
    .max(1000, "Số lượt tối đa là 1000"),
  reason: z.string().trim().max(200, "Lý do tối đa 200 ký tự"),
});
type GrantValues = z.infer<typeof grantSchema>;

const logSchema = z.object({
  action: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập action")
    .max(120, "Action tối đa 120 ký tự"),
  metadata: z
    .string()
    .trim()
    .refine((v) => {
      if (!v) return true;
      try {
        JSON.parse(v);
        return true;
      } catch {
        return false;
      }
    }, "Metadata phải là JSON hợp lệ"),
});
type LogValues = z.infer<typeof logSchema>;

/**
 * Admin concierge page — acts on behalf of an org.
 *
 * What this provides today:
 *   • Big red banner reminding the admin every action is audit-logged.
 *   • One-click "grant credits" out-of-band (offsets manual top-ups or comp).
 *   • Free-text "log act-as" — admin types what they're about to do; the
 *     server appends to ActivityLog with actorRole=ADMIN, orgId=:orgId.
 *
 * Real impersonation (swapping the JWT) is deliberately not implemented;
 * admins still call admin endpoints, which the BE already authorizes
 * cross-org. This page is the explicit audit-trail front-door.
 */
export function ActAsClient({ orgId }: { orgId: string }) {
  const grant = useAdminGrantCredits();
  const log = useAdminLogActAs();
  const { success: toastSuccess, error: toastError } = useToast();

  const grantForm = useForm<GrantValues>({
    resolver: zodResolver(grantSchema),
    mode: "onTouched",
    defaultValues: { amount: 3, reason: "" },
  });

  const logForm = useForm<LogValues>({
    resolver: zodResolver(logSchema),
    mode: "onTouched",
    defaultValues: { action: "", metadata: "" },
  });

  const onGrant = grantForm.handleSubmit(async (values) => {
    try {
      await grant.mutateAsync({
        orgId,
        amount: values.amount,
        reason: values.reason || "manual_admin_grant",
      });
      toastSuccess(`Đã cộng ${values.amount} lượt PV`);
      grantForm.reset({ amount: values.amount, reason: "" });
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Cộng lượt thất bại");
    }
  });

  const onLog = logForm.handleSubmit(async (values) => {
    const metadata = values.metadata
      ? (JSON.parse(values.metadata) as Record<string, unknown>)
      : undefined;
    try {
      await log.mutateAsync({ orgId, action: values.action, metadata });
      toastSuccess("Đã ghi audit log");
      logForm.reset({ action: "", metadata: "" });
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Ghi log thất bại");
    }
  });

  return (
    <div className="px-6 py-8">
      <div className="mb-6 rounded-lg border-2 border-ember bg-ember/10 px-4 py-3 text-sm font-medium text-ember">
        ⚠️ Bạn đang vận hành thay org #{orgId.slice(0, 8)}. Mọi action sẽ được
        ghi vào audit log.{" "}
        <Link to={`/orgs/${orgId}`} className="underline">
          Quay lại chi tiết org
        </Link>
      </div>

      <h1 className="mb-6 text-2xl font-extrabold tracking-tight text-ink">
        Concierge — org #{orgId.slice(0, 8)}
      </h1>

      <Card className="mb-4 p-4">
        <h2 className="mb-3 text-base font-semibold">Cộng lượt phỏng vấn</h2>
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-start"
          onSubmit={onGrant}
          noValidate
        >
          <FormField
            label="Số lượt"
            error={grantForm.formState.errors.amount?.message}
          >
            <Input
              type="number"
              min={1}
              max={1000}
              className="w-32"
              invalid={!!grantForm.formState.errors.amount}
              {...grantForm.register("amount", { valueAsNumber: true })}
            />
          </FormField>
          <FormField
            label="Lý do (audit)"
            error={grantForm.formState.errors.reason?.message}
          >
            <Input
              placeholder="VD: refund manual, comp khi server fail..."
              invalid={!!grantForm.formState.errors.reason}
              {...grantForm.register("reason")}
            />
          </FormField>
          <Button type="submit" disabled={grant.isPending} className="mt-6">
            Cộng
          </Button>
        </form>
      </Card>

      <Card className="p-4">
        <h2 className="mb-3 text-base font-semibold">Ghi audit log</h2>
        <p className="mb-3 text-xs text-ash">
          Mô tả ngắn action bạn sắp làm thay org. Append vào ActivityLog với
          prefix <code>admin.act_as.*</code>.
        </p>
        <form className="flex flex-col gap-3" onSubmit={onLog} noValidate>
          <FormField
            label="Action"
            error={logForm.formState.errors.action?.message}
          >
            <Input
              placeholder="VD: post_job_for_chu_quan_X"
              invalid={!!logForm.formState.errors.action}
              {...logForm.register("action")}
            />
          </FormField>
          <FormField
            label="Metadata (JSON, optional)"
            error={logForm.formState.errors.metadata?.message}
          >
            <Input
              placeholder='{"postingId":"...","via":"phone-call"}'
              invalid={!!logForm.formState.errors.metadata}
              {...logForm.register("metadata")}
            />
          </FormField>
          <div>
            <Button type="submit" variant="secondary" disabled={log.isPending}>
              Ghi log
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
